/**
 * Shared Styles for Lit Components
 * 
 * This module provides the shared styles pattern used by all components.
 * Components spread these styles to inherit theme variables and base styling.
 * 
 * Usage:
 * ```typescript
 * import { sharedStyles } from '../styles';
 * 
 * @customElement('my-component')
 * export class MyComponent extends LitElement {
 *   static styles = [...sharedStyles, css`
 *     // component-specific styles
 *   `];
 * }
 * ```
 * 
 * For components that only need specific styles:
 * ```typescript
 * import { themeStyles, playerStyles } from '../styles';
 * 
 * static styles = [themeStyles, playerStyles, css`...`];
 * ```
 */

import { CSSResult, unsafeCSS } from 'lit';

// Import CSS as strings for Lit's unsafeCSS
import darkCSS from './styles/dark.css?inline';
import lightCSS from './styles/light.css?inline';
import variablesCSS from './styles/variables.css?inline';

// Import semantic component styles
import playerCSS from './styles/player.css?inline';
import popoverCSS from './styles/popover.css?inline';
import uiCSS from './styles/ui.css?inline';

/**
 * Core CSS variables and theme definitions
 * These are always included in components for theming support
 */
export const themeStyles: CSSResult = unsafeCSS(`
${variablesCSS}
${lightCSS}
${darkCSS}
`);

/**
 * UI component styles (buttons, forms, cards, inputs)
 * Use for components that need standard UI elements
 */
export const uiStyles: CSSResult = unsafeCSS(uiCSS);

/**
 * Popover component styles (popovers, dropdowns, tooltips)
 * Use for components with floating UI elements
 */
export const popoverStyles: CSSResult = unsafeCSS(popoverCSS);

/**
 * Media player styles (control buttons, timelines, time displays)
 * Use for audio/video player components
 */
export const playerStyles: CSSResult = unsafeCSS(playerCSS);

/**
 * Shared styles array for Lit components
 * Spread this in your component's static styles property
 */
export const sharedStyles: CSSResult[] = [
  themeStyles,
  uiStyles,
  popoverStyles,
  playerStyles
];

export default sharedStyles;
